import React, { useState, useEffect } from 'react';
import { listenToRides, listenToDriverLocations } from '../firebase/database';

export default function Dashboard() {
  const [rides, setRides]                   = useState([]);
  const [driverLocations, setDriverLocations] = useState([]);
  const [isMobile, setIsMobile]             = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const unsub1 = listenToRides(setRides);
    const unsub2 = listenToDriverLocations(setDriverLocations);
    return () => { unsub1(); unsub2(); };
  }, []);

  const pending       = rides.filter(r => r.status === 'pending').length;
  const active        = rides.filter(r => ['assigned','accepted','in_progress'].includes(r.status)).length;
  const done          = rides.filter(r => r.status === 'completed').length;
  const onlineDrivers = driverLocations.filter(d => d.isOnline).length;
  const recent        = rides.slice(0, 8);

  const stats = [
    { label: 'Pending Rides',   value: pending,       color: '#f59e0b', bg: '#fffbeb', border: '#fde68a', icon: '⏳' },
    { label: 'Active Rides',    value: active,        color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe', icon: '🚗' },
    { label: 'Completed Today', value: done,          color: '#22c55e', bg: '#f0fdf4', border: '#bbf7d0', icon: '✅' },
    { label: 'Drivers Online',  value: onlineDrivers, color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe', icon: '📍' },
  ];

  return (
    <div>
      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
        gap: isMobile ? 10 : 16,
        marginBottom: isMobile ? 16 : 24
      }}>
        {stats.map(s => (
          <div key={s.label} style={{
            background: s.bg,
            borderRadius: 14, padding: isMobile ? '14px 16px' : '18px 22px',
            border: `1px solid ${s.border}`,
            transition: 'transform 0.15s',
            cursor: 'default'
          }}
          onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: isMobile ? 26 : 32, fontWeight: 800, color: s.color, lineHeight: 1 }}>
                  {s.value}
                </div>
                <div style={{ fontSize: isMobile ? 11 : 12, color: '#64748b', marginTop: 6, fontWeight: 500 }}>
                  {s.label}
                </div>
              </div>
              <div style={{ fontSize: isMobile ? 22 : 26, opacity: 0.8 }}>{s.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: isMobile ? 12 : 20
      }}>

        {/* Recent Rides */}
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{
            padding: '14px 18px', borderBottom: '1px solid #f1f5f9',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            background: '#fafafa'
          }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>
              Recent Ride Requests
            </div>
            <a href="#/rides" style={{
              fontSize: 12, color: '#3b82f6', textDecoration: 'none',
              fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4
            }}>
              View all →
            </a>
          </div>
          <div>
            {recent.length === 0 ? (
              <div style={{ padding: '36px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🚗</div>
                No ride requests yet
              </div>
            ) : (
              recent.map(ride => <RideRow key={ride.id} ride={ride} isMobile={isMobile} />)
            )}
          </div>
        </div>

        {/* Right Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 12 : 16 }}>

          {/* Quick Actions */}
          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: isMobile ? 14 : 18 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 12 }}>Quick Actions</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <QuickBtn href="#/rides"     icon="🚗" label="Manage Ride Requests"  color="#3b82f6" />
              <QuickBtn href="#/map"       icon="🗺" label="Open Live Driver Map"  color="#22c55e" />
              <QuickBtn href="#/drivers"   icon="👤" label="Add New Driver"        color="#8b5cf6" />
              <QuickBtn href="#/customers" icon="🧑‍💼" label="View Customers"       color="#f59e0b" />
              <QuickBtn href="#/reports"   icon="📊" label="Download Reports"      color="#ef4444" />
            </div>
          </div>

          {/* Online Drivers */}
          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: isMobile ? 14 : 18 }}>
            <div style={{
              fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 12,
              display: 'flex', alignItems: 'center', gap: 8
            }}>
              Online Drivers
              <span style={{
                background: onlineDrivers > 0 ? '#dcfce7' : '#f1f5f9',
                color: onlineDrivers > 0 ? '#166534' : '#94a3b8',
                fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20
              }}>
                {onlineDrivers} online
              </span>
            </div>
            {driverLocations.filter(d => d.isOnline).length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: 13, padding: '12px 0', textAlign: 'center' }}>
                <div style={{ fontSize: 24, marginBottom: 6 }}>🔴</div>
                No drivers online currently
              </div>
            ) : (
              driverLocations.filter(d => d.isOnline).map(d => {
                const activeRide = rides.find(r => r.driverId === d.driverId && ['assigned','accepted','in_progress'].includes(r.status));
                return (
                  <div key={d.driverId} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 0', borderBottom: '1px solid #f8fafc'
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%',
                      background: activeRide ? '#fef3c7' : '#dcfce7',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 15, fontWeight: 700,
                      color: activeRide ? '#92400e' : '#166534',
                      flexShrink: 0, border: `2px solid ${activeRide ? '#f59e0b' : '#22c55e'}`
                    }}>
                      {d.driverName?.charAt(0)?.toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{d.driverName}</div>
                      <div style={{ fontSize: 11, color: activeRide ? '#f59e0b' : '#22c55e', marginTop: 1 }}>
                        {activeRide ? `🚗 On ride: ${activeRide.customerName}` : '🟢 Available'}
                      </div>
                    </div>
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: activeRide ? '#f59e0b' : '#22c55e'
                    }} />
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function RideRow({ ride, isMobile }) {
  const statusConfig = {
    pending:     { bg: '#fef9c3', color: '#854d0e',  label: 'Pending'     },
    assigned:    { bg: '#dbeafe', color: '#1e40af',  label: 'Assigned'    },
    accepted:    { bg: '#dcfce7', color: '#15803d',  label: 'Accepted'    },
    in_progress: { bg: '#e0e7ff', color: '#4338ca',  label: 'In Progress' },
    completed:   { bg: '#f0fdf4', color: '#166534',  label: 'Completed'   },
    cancelled:   { bg: '#fee2e2', color: '#991b1b',  label: 'Cancelled'   },
  }[ride.status] || { bg: '#f1f5f9', color: '#475569', label: ride.status };

  return (
    <div style={{
      padding: '12px 18px', borderBottom: '1px solid #f8fafc',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      gap: 12, transition: 'background 0.1s'
    }}
    onMouseOver={e => e.currentTarget.style.background = '#f8fafc'}
    onMouseOut={e => e.currentTarget.style.background = 'transparent'}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 2 }}>
          {ride.customerName}
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          📍 {ride.pickupAddress}
        </div>
        {ride.driverName && (
          <div style={{ fontSize: 11, color: '#3b82f6', marginTop: 2 }}>
            🚗 {ride.driverName}
          </div>
        )}
      </div>
      <span style={{
        padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
        background: statusConfig.bg, color: statusConfig.color, whiteSpace: 'nowrap', flexShrink: 0
      }}>
        {statusConfig.label}
      </span>
    </div>
  );
}

function QuickBtn({ href, icon, label, color }) {
  return (
    <a href={href} style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px',
      background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
      textDecoration: 'none', color: '#0f172a', fontSize: 13, fontWeight: 500,
      transition: 'all 0.15s'
    }}
    onMouseOver={e => { e.currentTarget.style.background = `${color}10`; e.currentTarget.style.borderColor = `${color}40`; e.currentTarget.style.transform = 'translateX(3px)'; }}
    onMouseOut={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.transform = 'translateX(0)'; }}
    >
      <span style={{ fontSize: 18, width: 26, textAlign: 'center' }}>{icon}</span>
      <span>{label}</span>
      <span style={{ marginLeft: 'auto', color: '#94a3b8', fontSize: 16 }}>›</span>
    </a>
  );
}
